// import components
import { Typography } from '@mui/material';
import { StyledBoxFlexColumn } from 'components/shared/Box/Box.styles';
import {
  StyledAddRoundButton,
  StyledDeleteRoundsButton,
  StyledRoundsBox,
  StyledRoundsButton,
  StyledRoundsLeftBox,
  StyledSettingByTypeBox
} from '../AIMatch.styles';
import PhaseMetaData from './PhaseMetaData';
import { AIMatchSettings } from './AIMatchSettings';

// import hooks
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// import types
import { AIMatchSettingsType } from 'components/types';
import { AskRoundSettingsResponse } from 'generated/index';
import { MouseEvent } from 'react';
import { AIMatchRoundSettingsProps } from './AIMatchRoundSettings.types';
import { AIMatchForm } from '../AIMatch.types';
import { Icon } from '@sdb/planning-ui';

const AIMatchRoundSettings = ({
  isLoading,
  setCurrentRound,
  defaultRoundValues,
  hasPlanningEntities,
  hasDutyAutoAskEmployeesAdvanced
}: AIMatchRoundSettingsProps) => {
  const { watch, getValues, setValue } = useFormContext<AIMatchForm>();
  const { t } = useTranslation('common', { keyPrefix: 'aiMatchSettings' });

  // rounds CRUD
  const addRound = (event: MouseEvent<HTMLButtonElement>, index: number) => {
    event.preventDefault();
    setValue('rounds', [...getValues().rounds, defaultRoundValues]);

    const newRound = getValues().rounds[getValues().rounds.length - 1];
    setCurrentRound({ index, round: newRound });
  };

  const editRound = (
    event: MouseEvent<HTMLButtonElement>,
    index: number,
    round: AskRoundSettingsResponse
  ) => {
    event.preventDefault();
    setCurrentRound({ index, round });
  };

  const removeRound = (event: MouseEvent<HTMLButtonElement>, index: number) => {
    event.preventDefault();
    setValue(
      'rounds',
      getValues().rounds.filter((_, i) => i !== index),
      { shouldDirty: true }
    );
  };

  return (
    <StyledBoxFlexColumn>
      <Typography variant="h6">{t('rounds')}</Typography>

      <StyledSettingByTypeBox>
        <AIMatchSettings
          type={AIMatchSettingsType.FLEX_ENABLED}
          isLoading={isLoading}
        />
        <AIMatchSettings
          type={AIMatchSettingsType.NON_FLEX_ENABLED}
          isLoading={isLoading}
        />
      </StyledSettingByTypeBox>
      <AIMatchSettings
        type={AIMatchSettingsType.ROUND_MINUTES_BEFORE_START_LIMIT}
        isLoading={isLoading}
      />

      <StyledBoxFlexColumn>
        {watch('rounds').map(
          (round: AskRoundSettingsResponse, index: number) => {
            const enabledOptions: string[] = Object.entries(round)
              .filter(([, value]) => value && typeof value === 'boolean')
              .map(([key]) => t(`roundType.${key}`));
            return (
              <StyledRoundsBox key={index}>
                <StyledRoundsButton
                  type="button"
                  variant="outlined"
                  color="dark"
                  fullWidth
                  onClick={(event) => editRound(event, index, round)}
                  endIcon={<Icon type="pen" color="action" fontSize="small" />}
                >
                  <StyledRoundsLeftBox>
                    <Typography component="span">
                      {t('roundNr', { number: index + 1 })}
                    </Typography>

                    <PhaseMetaData round={round} />
                  </StyledRoundsLeftBox>

                  <Typography variant="body2" color="textSecondary">
                    {enabledOptions.join(', ')}
                  </Typography>
                </StyledRoundsButton>

                <StyledDeleteRoundsButton
                  type="button"
                  variant="text"
                  onClick={(event) => removeRound(event, index)}
                  disabled={
                    !hasPlanningEntities && !hasDutyAutoAskEmployeesAdvanced
                  }
                >
                  <Icon type="xmark" color="inherit" fontSize="small" />
                </StyledDeleteRoundsButton>
              </StyledRoundsBox>
            );
          }
        )}

        <StyledAddRoundButton
          type="button"
          variant="contained"
          color="primary"
          onClick={(event: MouseEvent<HTMLButtonElement>) =>
            addRound(event, getValues().rounds.length)
          }
          startIcon={<Icon type="plus" color="inherit" />}
          disabled={!hasPlanningEntities && !hasDutyAutoAskEmployeesAdvanced}
        >
          {t('addRound')}
        </StyledAddRoundButton>
      </StyledBoxFlexColumn>
    </StyledBoxFlexColumn>
  );
};

export default AIMatchRoundSettings;
